import { Box, Code, Paragraph, Text } from "@hightouch/htui";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  mentionKey,
  parseTarget,
  splitBodyOnMentions,
  type MentionNames,
} from "@hightouch/lib/llm/asset-comments/mention-tokens";
import { Link } from "src/router";

/**
 * Gaps as bottom margins only, so margins collapse cleanly. List items sit
 * closer than blocks, so a list reads as one unit. Compact is for a page that
 * lists many threads in full, where the default spacing pushes most of them
 * below the fold.
 */
const SPACING = {
  default: { block: 3, listItem: 2, listIndent: 5 },
  compact: { block: 1, listItem: 0, listIndent: 4 },
} as const;

export type CommentDensity = keyof typeof SPACING;

const NO_NAMES: MentionNames = new Map();

type Props = {
  body: string;
  /** Current names of the body's mention targets. */
  mentionNames?: MentionNames;
  /**
   * Reads every newline as a line break. The composer separates the lines a
   * person typed with a single `\n`, which markdown would join into one line.
   */
  hasHardLineBreaks?: boolean;
  density?: CommentDensity;
};

/**
 * A comment body rendered as markdown, for people's comments and agent replies.
 * `@`-mentions keep their highlight and show the target's current name.
 *
 * Its own renderer rather than the LLM `Markdown`: that one is built for the
 * transcript's 14px prose and carries charts, artifact links, and SQL
 * formatting that a comment rail has no use for.
 */
export const MarkdownCommentBody = ({
  body,
  mentionNames = NO_NAMES,
  hasHardLineBreaks = false,
  density = "default",
}: Props) => {
  const { block, listItem, listIndent } = SPACING[density];
  const heading = ({ children }: { children?: React.ReactNode }) => (
    <Heading spacing={block}>{children}</Heading>
  );

  return (
    // Trailing block margin is the rail's to own, not the last paragraph's.
    <Box sx={{ "& > *:last-child": { mb: 0 } }}>
      <ReactMarkdown
        remarkPlugins={
          hasHardLineBreaks ? [remarkGfm, remarkHardLineBreaks] : [remarkGfm]
        }
        // The default drops unknown schemes, which would strip the mention targets.
        urlTransform={(url) =>
          parseTarget(url) ? url : defaultUrlTransform(url)
        }
        components={{
          p: ({ children }) => (
            <Paragraph size="md" color="text.secondary" mb={block}>
              {children}
            </Paragraph>
          ),
          strong: ({ children }) => (
            <Text size="md" fontWeight="semibold" color="text.primary">
              {children}
            </Text>
          ),
          // htui Text has no italic surface, so the native tag carries it;
          // inline, or it would break the line it sits in.
          em: ({ children }) => (
            <Box as="em" display="inline">
              {children}
            </Box>
          ),
          a: ({ href, children }) =>
            href && parseTarget(href) ? (
              <Text size="md" color="text.accent" fontWeight="medium">
                {children}
              </Text>
            ) : (
              <Link
                href={href ?? "#"}
                fontSize="inherit"
                isExternal={!href?.startsWith("/")}
              >
                {children}
              </Link>
            ),
          ul: ({ children }) => (
            <Box
              as="ul"
              pl={listIndent}
              mb={block}
              style={{ listStyleType: "disc" }}
            >
              {children}
            </Box>
          ),
          ol: ({ children, ...props }) => (
            <Box
              as="ol"
              pl={listIndent}
              mb={block}
              start={props.start}
              style={{ listStyleType: "decimal" }}
            >
              {children}
            </Box>
          ),
          li: ({ children }) => (
            // No Chakra prop reaches ::marker, so the muting goes through sx.
            // Loose lists wrap item text in <p>; stripping its bottom margin
            // keeps item gaps coming from the li alone.
            <Box
              as="li"
              mb={listItem}
              sx={{
                "&::marker": { color: "text.tertiary" },
                "& p:last-child, & ul:last-child, & ol:last-child": { mb: 0 },
              }}
            >
              <Text as="span" size="sm" color="text.secondary">
                {children}
              </Text>
            </Box>
          ),
          // Headings are rare in a reply and there's no room for a scale, so
          // every level reads as one emphasized line.
          h1: heading,
          h2: heading,
          h3: heading,
          h4: heading,
          h5: heading,
          h6: heading,
          blockquote: ({ children }) => (
            // Chakra's reset leaves blockquote completely unstyled.
            <Box
              as="blockquote"
              borderLeft="2px solid"
              borderColor="base.border"
              pl={3}
              mb={block}
              sx={{ "& > *:last-child": { mb: 0 } }}
            >
              {children}
            </Box>
          ),
          code: ({ children }) => <Code>{children}</Code>,
          pre: ({ children }) => (
            <Box
              as="pre"
              mb={block}
              p={2}
              borderRadius="md"
              overflowX="auto"
              bg="base.background"
              fontSize="xs"
            >
              {children}
            </Box>
          ),
          hr: () => <Box as="hr" my={block} />,
          // Chakra's reset leaves tables to the browser: 16px text, centered headers and no row lines.
          table: ({ children }) => (
            <Box overflowX="auto" mb={block}>
              <Box as="table" width="100%" sx={{ borderCollapse: "collapse" }}>
                {children}
              </Box>
            </Box>
          ),
          th: ({ children }) => (
            <Box
              as="th"
              textAlign="left"
              verticalAlign="bottom"
              px={2}
              py={1.5}
              borderBottomWidth="1px"
              borderColor="base.border"
              _first={{ pl: 0 }}
            >
              <Text size="sm" fontWeight="medium" color="text.primary">
                {children}
              </Text>
            </Box>
          ),
          td: ({ children }) => (
            <Box
              as="td"
              verticalAlign="top"
              px={2}
              py={1.5}
              borderBottomWidth="1px"
              borderColor="base.divider"
              _first={{ pl: 0 }}
            >
              <Text size="sm" color="text.secondary">
                {children}
              </Text>
            </Box>
          ),
        }}
      >
        {withMentionLinks(body, mentionNames)}
      </ReactMarkdown>
    </Box>
  );
};

const Heading = ({
  children,
  spacing,
}: {
  children?: React.ReactNode;
  spacing: number;
}) => (
  <Box mt={spacing} mb={1}>
    <Text size="md" fontWeight="semibold" color="text.primary">
      {children}
    </Text>
  </Box>
);

/** Rewrites each mention token as a markdown link that reads `@Name`, with the current name. */
function withMentionLinks(body: string, names: MentionNames): string {
  return splitBodyOnMentions(body, names)
    .map((segment) =>
      segment.kind === "text"
        ? segment.text
        : `[@${segment.label.replace(/[[\]\\]/g, "\\$&")}](${mentionKey(segment.mention)})`,
    )
    .join("");
}

type MdastNode = { type: string; value?: string; children?: MdastNode[] };

/** Splits text on newlines into hard breaks, like GitHub comments. Code is untouched. */
function remarkHardLineBreaks() {
  return (tree: MdastNode) => splitOnNewlines(tree);
}

function splitOnNewlines(node: MdastNode) {
  if (!node.children) return;
  node.children = node.children.flatMap((child) => {
    if (child.type !== "text" || !child.value?.includes("\n")) {
      splitOnNewlines(child);
      return [child];
    }
    return child.value
      .split("\n")
      .flatMap((line, index) =>
        index === 0
          ? [{ type: "text", value: line }]
          : [{ type: "break" }, { type: "text", value: line }],
      );
  });
}
